import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Lock } from "lucide-react";
import { point as turfPoint, polygon as turfPolygon } from "@turf/helpers";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { PageHeader } from "../components/shared/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { BuilderMap } from "../components/routes/builder/BuilderMap";
import { CountersBar } from "../components/routes/builder/CountersBar";
import { RouteTabsList } from "../components/routes/builder/RouteTabsList";
import { NewRouteDialog } from "../components/routes/builder/NewRouteDialog";
import { AutoPlanPanel } from "../components/routes/builder/AutoPlanPanel";
import { ActiveRoutePanel } from "../components/routes/builder/ActiveRoutePanel";
import { UnassignedList } from "../components/routes/builder/UnassignedList";
import { DailySummaryPanel } from "../components/routes/builder/DailySummaryPanel";
import { SavedRoutesPanel } from "../components/routes/builder/SavedRoutesPanel";
import { FinalizeRoutesDialog } from "../components/routes/builder/FinalizeRoutesDialog";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { colorForIndex } from "../lib/routeColors";
import { todayLocal as today } from "../lib/utils";

function polygonContains(latlngs, point) {
  const ring = latlngs.map(([lat, lon]) => [lon, lat]);
  if (ring.length < 3) return false;
  if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) ring.push(ring[0]);
  const poly = turfPolygon([ring]);
  return booleanPointInPolygon(turfPoint(point), poly);
}

export default function RouteBuilder() {
  const navigate = useNavigate();
  const [date, setDate] = useState(today());
  const [finalizeStatus, setFinalizeStatus] = useState(null);
  const [pendingDate, setPendingDate] = useState(null);
  const [outlet, setOutlet] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [, setCandidates] = useState(null);
  const [savedRoutes, setSavedRoutes] = useState({ loading: true, items: [] });
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);
  const [tempRoutes, setTempRoutes] = useState([]);
  const [activeTempId, setActiveTempId] = useState(null);
  const [drawActive, setDrawActive] = useState(false);
  const [mode, setMode] = useState("manual");
  const [newRouteOpen, setNewRouteOpen] = useState(false);
  const [autoPlanOpen, setAutoPlanOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizingAll, setOptimizingAll] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [highlightedTempId, setHighlightedTempId] = useState(null);
  const [previewEndpoints, setPreviewEndpoints] = useState([]);
  const [hoveredDeliveryId, setHoveredDeliveryId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [routeDeleteTarget, setRouteDeleteTarget] = useState(null);
  const [deletingRoute, setDeletingRoute] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const nextTempId = useRef(1);

  useEffect(() => {
    apiClient.get("/outlet/settings").then(({ data }) => setOutlet(data)).catch(() => setOutlet(null));
    apiClient.get("/drivers", { params: { limit: 200 } }).then(({ data }) => {
      setDrivers(data.items.filter((d) => d.user_status === 10 && !d.is_deleted));
    }).catch(() => setDrivers([]));
  }, []);

  const loadRequestRef = useRef(null);

  const loadForDate = useCallback((d) => {
    const requestId = Symbol(d);
    loadRequestRef.current = requestId;
    setLoadingDeliveries(true);
    setSavedRoutes((s) => ({ ...s, loading: true }));
    Promise.all([
      apiClient.get("/deliveries", { params: { date: d, limit: 1000, exclude_pickup: true } }),
      apiClient.get("/deliveries/generate/candidates", { params: { date: d, type: 1 } }).catch(() => ({ data: null })),
      apiClient.get("/deliveries/generate/candidates", { params: { date: d, type: 2 } }).catch(() => ({ data: null })),
      apiClient.get("/routes", { params: { date: d, limit: 200 } }),
    ]).then(([delRes, cand1Res, cand2Res, routesRes]) => {
      if (loadRequestRef.current !== requestId) return; // superseded by a newer date change
      setDeliveries(delRes.data.items);
      setCandidates({ pickup: cand1Res.data, delivery: cand2Res.data });
      setSavedRoutes({ loading: false, items: routesRes.data.items });
    }).catch((error) => {
      if (loadRequestRef.current !== requestId) return;
      toast.error(getApiErrorMessage(error, "Could not load deliveries for this date."));
      setDeliveries([]);
      setSavedRoutes({ loading: false, items: [] });
    }).finally(() => {
      if (loadRequestRef.current === requestId) setLoadingDeliveries(false);
    });
  }, []);

  useEffect(() => { loadForDate(date); }, [date, loadForDate]);

  useEffect(() => {
    apiClient.get("/deliveries/finalize-status", { params: { date } })
      .then(({ data }) => setFinalizeStatus(data))
      .catch(() => setFinalizeStatus(null));
  }, [date]);

  useEffect(() => {
    setSuggestionsLoading(true);
    apiClient.get("/routes/auto-plan/suggestions", { params: { date } })
      .then(({ data }) => setSuggestions(data.drivers))
      .catch(() => setSuggestions([]))
      .finally(() => setSuggestionsLoading(false));
  }, [date]);

  const suggestionMap = useMemo(() => new Map(suggestions.map((s) => [s.driver_id, s])), [suggestions]);

  const hasUnsavedPlan = tempRoutes.some((r) => {
    if (r.existingRouteId) {
      const original = r.originalDeliveryIds || new Set();
      return r.deliveryIds.size !== original.size || [...r.deliveryIds].some((id) => !original.has(id));
    }
    return r.deliveryIds.size > 0;
  });

  const requestDateChange = (newDate) => {
    if (hasUnsavedPlan) setPendingDate(newDate);
    else setDate(newDate);
  };

  const confirmDateChange = () => {
    setTempRoutes([]);
    setActiveTempId(null);
    setDate(pendingDate);
    setPendingDate(null);
  };

  const deliveryIndex = useMemo(() => new Map(deliveries.map((d) => [d.id, d])), [deliveries]);

  const ownerMap = useMemo(() => {
    const m = new Map();
    tempRoutes.forEach((r) => r.deliveryIds.forEach((id) => m.set(id, r.tempId)));
    return m;
  }, [tempRoutes]);

  // Routes currently opened for in-session editing — their original stops are
  // treated as "free" (not locked) since nothing is persisted until Save Changes.
  const editingRouteIds = useMemo(
    () => new Set(tempRoutes.filter((r) => r.existingRouteId).map((r) => r.existingRouteId)),
    [tempRoutes]
  );

  const unassignedDeliveries = useMemo(
    () => deliveries.filter((d) => !ownerMap.has(d.id) && (!d.route_id || editingRouteIds.has(d.route_id))),
    [deliveries, ownerMap, editingRouteIds]
  );

  // Color swatch per saved route on this date, for the locked-pin map rendering
  // and the Existing Routes panel — prefers the driver's own color.
  const routeColorMap = useMemo(() => {
    const m = new Map();
    savedRoutes.items.forEach((r) => {
      const driver = drivers.find((dr) => dr.id === r.driver_id);
      m.set(r.id, driver?.color || colorForIndex(r.id % 12));
    });
    return m;
  }, [savedRoutes.items, drivers]);

  const activeRoute = tempRoutes.find((r) => r.tempId === activeTempId) || null;

  const handleCreateRoute = (driverId) => {
    const driver = drivers.find((d) => d.id === driverId);
    const tempId = nextTempId.current++;
    const suggestedMaxDistance = suggestionMap.get(driverId)?.suggested_max_distance_km;
    const route = {
      tempId, driverId, driverName: driver?.name || `Driver #${driverId}`,
      driverEndpoint: driver ? { latitude: driver.latitude, longitude: driver.longitude, address: driver.address } : null,
      color: driver?.color || colorForIndex(tempId - 1), deliveryIds: new Set(), startTime: "09:00", optimized: null, objective: "time", useRealDistance: true, serviceTimeMinutes: 3, maxDistanceKm: suggestedMaxDistance ?? "", maxDurationHours: "", variant: 1,
    };
    setTempRoutes((rs) => [...rs, route]);
    setActiveTempId(tempId);
  };

  const handleAssignToRoute = (deliveryId, tempId) => {
    setTempRoutes((rs) => rs.map((r) => {
      if (r.tempId !== tempId) return r;
      const next = new Set(r.deliveryIds);
      next.add(deliveryId);
      return { ...r, deliveryIds: next, optimized: null };
    }));
  };

  const handleAutoPlanGenerated = (data, serviceTimeByDriver, maxDistanceByDriver) => {
    const existingDriverIds = new Set(tempRoutes.map((r) => r.driverId));
    const skippedDuplicates = [];
    const newRoutes = data.routes.filter((r) => {
      if (r.delivery_ids.length === 0) return false;
      if (existingDriverIds.has(r.driver_id)) { skippedDuplicates.push(r.driver_name || `#${r.driver_id}`); return false; }
      return true;
    }).map((r) => {
      const tempId = nextTempId.current++;
      const driver = drivers.find((d) => d.id === r.driver_id);
      return {
        tempId, driverId: r.driver_id, driverName: r.driver_name || driver?.name || `Driver #${r.driver_id}`,
        driverEndpoint: driver ? { latitude: driver.latitude, longitude: driver.longitude, address: driver.address } : null,
        color: driver?.color || colorForIndex(tempId - 1),
        deliveryIds: new Set(r.delivery_ids), startTime: "09:00", optimized: null, objective: "time",
        useRealDistance: true, serviceTimeMinutes: serviceTimeByDriver?.[r.driver_id] ?? 3,
        maxDistanceKm: maxDistanceByDriver?.[r.driver_id] ?? "", maxDurationHours: "", variant: 1,
      };
    });
    if (newRoutes.length === 0) {
      toast.error(skippedDuplicates.length > 0
        ? `${skippedDuplicates.join(", ")} already ${skippedDuplicates.length === 1 ? "has" : "have"} a draft route on this screen.`
        : "No unassigned deliveries could be planned for the selected drivers on this date.");
      return false;
    }
    setTempRoutes((rs) => [...rs, ...newRoutes]);
    setActiveTempId(newRoutes[0].tempId);
    const totalStops = newRoutes.reduce((sum, r) => sum + r.deliveryIds.size, 0);
    toast.success(`Generated ${newRoutes.length} route(s) with ${totalStops} stop(s) total.${skippedDuplicates.length > 0 ? ` Skipped ${skippedDuplicates.join(", ")} (already has a draft route).` : ""} Review, optimize, and finalize when ready.`);
    return true;
  };

  const updateActiveRoute = (fn) => {
    setTempRoutes((rs) => rs.map((r) => (r.tempId === activeTempId ? fn(r) : r)));
  };

  const handlePolygonComplete = (latlngs) => {
    setDrawActive(false);
    if (!activeRoute) return;
    const matched = deliveries.filter((d) => {
      if (ownerMap.has(d.id) || !d.latitude || !d.longitude) return false;
      if (d.route_id && !editingRouteIds.has(d.route_id)) return false; // locked to another saved route
      return polygonContains(latlngs, [parseFloat(d.longitude), parseFloat(d.latitude)]);
    }).map((d) => d.id);
    if (matched.length === 0) { toast.info("No unassigned deliveries fell inside that area."); return; }
    updateActiveRoute((r) => ({ ...r, deliveryIds: new Set([...r.deliveryIds, ...matched]), optimized: null }));
    toast.success(`Added ${matched.length} stop(s) to Route ${activeTempId}`);
  };

  const handleMarkerAction = (deliveryId, action) => {
    if (!activeRoute) return;
    updateActiveRoute((r) => {
      const next = new Set(r.deliveryIds);
      if (action === "add") next.add(deliveryId); else next.delete(deliveryId);
      return { ...r, deliveryIds: next, optimized: null };
    });
  };

  const moveDeliveryBetweenRoutes = (deliveryId, fromTempId, toTempId) => {
    setTempRoutes((rs) => rs.map((r) => {
      if (r.tempId === fromTempId) {
        const next = new Set(r.deliveryIds); next.delete(deliveryId);
        return { ...r, deliveryIds: next, optimized: null };
      }
      if (r.tempId === toTempId) {
        const next = new Set(r.deliveryIds); next.add(deliveryId);
        return { ...r, deliveryIds: next, optimized: null };
      }
      return r;
    }));
  };

  const handleMarkerDrop = (deliveryId, targetTempId) => {
    const fromTempId = ownerMap.get(deliveryId);
    if (!fromTempId || fromTempId === targetTempId) return;
    moveDeliveryBetweenRoutes(deliveryId, fromTempId, targetTempId);
    const targetRoute = tempRoutes.find((r) => r.tempId === targetTempId);
    const label = targetRoute?.existingRouteId ? `#${targetRoute.existingRouteId}` : targetTempId;
    toast.success(`Moved to Route ${label}${targetRoute ? ` — ${targetRoute.driverName}` : ""}`, {
      action: { label: "Undo", onClick: () => moveDeliveryBetweenRoutes(deliveryId, targetTempId, fromTempId) },
    });
  };

  const optimizeRoute = async (route, variant) => {
    const jobs = [...route.deliveryIds].map((id) => {
      const d = deliveryIndex.get(id);
      return { id, location: [parseFloat(d.longitude), parseFloat(d.latitude)] };
    });
    const vehicle = {
      id: route.driverId,
      start: [parseFloat(outlet.longitude), parseFloat(outlet.latitude)],
      end: [parseFloat(route.driverEndpoint.longitude), parseFloat(route.driverEndpoint.latitude)],
      max_tasks: jobs.length,
    };
    const { data } = await apiClient.post("/routes/optimize", {
      jobs, vehicles: [vehicle], objective: route.objective, use_real_distance: route.useRealDistance,
      service_time_minutes: route.serviceTimeMinutes,
      max_distance_km: route.maxDistanceKm ? Number(route.maxDistanceKm) : null,
      max_duration_hours: route.maxDurationHours ? Number(route.maxDurationHours) : null,
      variant,
    });
    const optRoute = data.routes[0];
    setTempRoutes((rs) => rs.map((r) => (r.tempId === route.tempId ? {
      ...r, variant,
      optimized: {
        steps: optRoute?.steps || [], distance: optRoute?.distance ?? 0, duration: optRoute?.duration ?? 0,
        mode: data.mode, unassigned: data.unassigned, distanceSource: optRoute?.distance_source || "haversine",
      },
    } : r)));
    return optRoute?.distance_source === "ors_matrix" || optRoute?.distance_source === "osrm_matrix";
  };

  const handleOptimize = async (variantOverride) => {
    if (!activeRoute || !outlet?.latitude || !outlet?.longitude) {
      toast.error("Restaurant location is not configured — cannot optimize.");
      return;
    }
    const variant = variantOverride || 1;
    setOptimizing(true);
    try {
      const usedReal = await optimizeRoute(activeRoute, variant);
      if (activeRoute.useRealDistance && !usedReal) {
        toast.warning(`Route ${activeRoute.tempId} optimized, but fell back to straight-line distance (ORS unavailable or too many stops).`);
      } else {
        toast.success(`Route ${activeRoute.tempId} optimized using ${usedReal ? "real road distance" : "straight-line distance"} (variant ${variant} of 6)`);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not optimize this route."));
    } finally {
      setOptimizing(false);
    }
  };

  const handleReoptimize = () => {
    if (!activeRoute) return;
    const current = activeRoute.variant || 1;
    handleOptimize(current >= 6 ? 1 : current + 1);
  };

  const handleOptimizeAll = async () => {
    if (!outlet?.latitude || !outlet?.longitude) {
      toast.error("Restaurant location is not configured — cannot optimize.");
      return;
    }
    const targets = tempRoutes.filter((r) => r.deliveryIds.size > 0 && r.driverEndpoint?.latitude && r.driverEndpoint?.longitude);
    if (targets.length === 0) {
      toast.info("No draft routes with stops to optimize yet.");
      return;
    }
    setOptimizingAll(true);
    let succeeded = 0;
    let fellBack = 0;
    let failed = 0;
    for (const route of targets) {
      try {
        const usedReal = await optimizeRoute(route, route.variant || 1);
        succeeded += 1;
        if (route.useRealDistance && !usedReal) fellBack += 1;
      } catch (error) {
        failed += 1;
      }
    }
    setOptimizingAll(false);
    if (failed > 0) {
      toast.warning(`Optimized ${succeeded} of ${targets.length} route(s) — ${failed} failed.${fellBack > 0 ? ` ${fellBack} fell back to straight-line distance.` : ""}`);
    } else {
      toast.success(`Optimized all ${succeeded} route(s).${fellBack > 0 ? ` ${fellBack} fell back to straight-line distance (ORS unavailable or too many stops).` : ""}`);
    }
  };

  const handleDeleteRoute = () => {
    if (!activeRoute) return;
    setRouteDeleteTarget({ routeId: activeRoute.existingRouteId || null, tempId: activeRoute.tempId });
  };

  const handleCancelEditing = () => {
    if (!activeRoute?.existingRouteId) return;
    setTempRoutes((rs) => rs.filter((r) => r.tempId !== activeRoute.tempId));
    setActiveTempId(null);
  };

  const handleEditExistingRoute = (route) => {
    if (tempRoutes.some((r) => r.existingRouteId === route.id)) {
      toast.info(`Route #${route.id} is already open for editing.`);
      return;
    }
    const driver = drivers.find((d) => d.id === route.driver_id);
    const routeDeliveryIds = new Set(deliveries.filter((d) => d.route_id === route.id).map((d) => d.id));
    const tempId = nextTempId.current++;
    setTempRoutes((rs) => [...rs, {
      tempId, driverId: route.driver_id, driverName: route.driver_name || driver?.name || `Driver #${route.driver_id}`,
      driverEndpoint: driver ? { latitude: driver.latitude, longitude: driver.longitude, address: driver.address } : null,
      color: driver?.color || colorForIndex(tempId - 1),
      deliveryIds: routeDeliveryIds, startTime: "09:00", optimized: null, objective: "time",
      useRealDistance: true, serviceTimeMinutes: 3, maxDistanceKm: "", maxDurationHours: "", variant: 1,
      existingRouteId: route.id, originalDeliveryIds: new Set(routeDeliveryIds),
    }]);
    setActiveTempId(tempId);
  };

  const handleSelectSavedRouteFromMap = (routeId) => {
    const existing = tempRoutes.find((r) => r.existingRouteId === routeId);
    if (existing) { setActiveTempId(existing.tempId); return; }
    const route = savedRoutes.items.find((r) => r.id === routeId);
    if (route) handleEditExistingRoute(route);
  };

  const handleDeleteSavedRoute = (route) => {
    setRouteDeleteTarget({ routeId: route.id, tempId: null });
  };

  const confirmDeleteRoute = async () => {
    if (!routeDeleteTarget) return;
    if (!routeDeleteTarget.routeId) {
      setTempRoutes((rs) => rs.filter((r) => r.tempId !== routeDeleteTarget.tempId));
      setActiveTempId(null);
      setRouteDeleteTarget(null);
      return;
    }
    setDeletingRoute(true);
    try {
      await apiClient.delete(`/routes/${routeDeleteTarget.routeId}`);
      toast.success(`Route #${routeDeleteTarget.routeId} deleted — its stops are back in the unassigned pool for ${date}.`);
      if (routeDeleteTarget.tempId) {
        setTempRoutes((rs) => rs.filter((r) => r.tempId !== routeDeleteTarget.tempId));
        setActiveTempId(null);
      }
      setRouteDeleteTarget(null);
      loadForDate(date);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete this route."));
    } finally {
      setDeletingRoute(false);
    }
  };

  const handleSaveExistingRouteChanges = async () => {
    if (!activeRoute?.existingRouteId) return;
    const original = activeRoute.originalDeliveryIds || new Set();
    const add_delivery_ids = [...activeRoute.deliveryIds].filter((id) => !original.has(id));
    const remove_delivery_ids = [...original].filter((id) => !activeRoute.deliveryIds.has(id));
    if (add_delivery_ids.length === 0 && remove_delivery_ids.length === 0) {
      toast.info("No changes to save.");
      return;
    }
    setSavingChanges(true);
    try {
      await apiClient.post(`/routes/${activeRoute.existingRouteId}/apply-changes`, { add_delivery_ids, remove_delivery_ids });
      toast.success(`Saved changes to Route #${activeRoute.existingRouteId}.`);
      setTempRoutes((rs) => rs.filter((r) => r.tempId !== activeRoute.tempId));
      setActiveTempId(null);
      loadForDate(date);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not save changes to this route."));
    } finally {
      setSavingChanges(false);
    }
  };

  const isToday = date === today();
  const blockedForToday = isToday && finalizeStatus != null && !finalizeStatus.finalized;

  const handleAutoAssign = async () => {
    if (tempRoutes.length === 0) return;
    const pool = unassignedDeliveries.filter((d) => d.latitude && d.longitude).map((d) => d.id);
    if (pool.length === 0) {
      toast.info("No unassigned deliveries left to distribute.");
      return;
    }
    setAutoAssigning(true);
    try {
      const driversPayload = tempRoutes.map((r) => ({
        driver_id: r.driverId,
        endpoint: r.driverEndpoint
          ? [parseFloat(r.driverEndpoint.longitude), parseFloat(r.driverEndpoint.latitude)]
          : [parseFloat(outlet?.longitude || 0), parseFloat(outlet?.latitude || 0)],
        assigned_delivery_ids: [...r.deliveryIds],
      }));
      const { data } = await apiClient.post("/routes/auto-assign", {
        delivery_ids: pool, drivers: driversPayload,
      });
      setTempRoutes((rs) => rs.map((r) => {
        const added = data.assignments[String(r.driverId)] || [];
        if (added.length === 0) return r;
        return { ...r, deliveryIds: new Set([...r.deliveryIds, ...added]), optimized: null };
      }));
      const totalAssigned = Object.values(data.assignments).reduce((sum, arr) => sum + arr.length, 0);
      if (data.unassigned.length > 0) {
        toast.warning(`Auto-assigned ${totalAssigned} stop(s). ${data.unassigned.length} left unassigned — too far from any driver's route, assign those manually.`);
      } else {
        toast.success(`Auto-assigned ${totalAssigned} stop(s) across ${tempRoutes.length} driver route(s).`);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not auto-assign deliveries."));
    } finally {
      setAutoAssigning(false);
    }
  };

  const totalEligible = deliveries.length;
  const assigned = tempRoutes.reduce((sum, r) => sum + r.deliveryIds.size, 0);
  const left = unassignedDeliveries.length;
  const current = activeRoute?.deliveryIds.size || 0;
  const usedDriverIds = tempRoutes.map((r) => r.driverId);

  return (
    <div data-testid="route-builder-page">
      <PageHeader
        title="Route Builder"
        description={mode === "automatic" ? "Pick drivers and let the system copy their routes from their most recent route day, then review/edit before saving." : "Manually group deliveries into driver territories, then optimize stop order inside each."}
        actions={<Button variant="secondary" onClick={() => navigate("/routes")} data-testid="route-builder-back-button"><ArrowLeft size={16} strokeWidth={1.5} /> Back to Routes</Button>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-text-muted">Route Date</label>
          <Input type="date" className="w-40" value={date} onChange={(e) => requestDateChange(e.target.value)} data-testid="builder-date-input" />
        </div>
        <div className="rounded-md border border-border bg-surface px-3 py-2 text-xs" data-testid="restaurant-start-info">
          <p className="uppercase tracking-wide text-text-muted">Restaurant Start</p>
          <p className="font-medium text-text-main">{outlet ? outlet.name : "Loading..."}</p>
        </div>
        <div className="inline-flex rounded-md border border-border bg-surface p-1" data-testid="route-mode-toggle">
          <button
            type="button" onClick={() => setMode("manual")}
            className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${mode === "manual" ? "bg-primary text-white" : "text-text-muted hover:text-text-main"}`}
            data-testid="route-mode-manual-button"
          >Manual</button>
          <button
            type="button" onClick={() => setMode("automatic")}
            className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${mode === "automatic" ? "bg-primary text-white" : "text-text-muted hover:text-text-main"}`}
            data-testid="route-mode-automatic-button"
          >Automatic</button>
        </div>
        <Button variant="ghost" size="sm" onClick={() => loadForDate(date)} data-testid="builder-refresh-button">
          <RefreshCw size={14} strokeWidth={1.5} /> Refresh
        </Button>
      </div>

      {blockedForToday ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/40 bg-warning-bg px-4 py-3 text-sm text-text-main" data-testid="route-builder-finalize-required">
          <span className="flex items-center gap-2 font-medium"><Lock size={16} strokeWidth={2} /> Today's deliveries aren't finalized yet. Finalize them to build routes.</span>
          <Button variant="accent" size="sm" onClick={() => navigate("/subscriptions")} data-testid="route-builder-go-finalize">Go to Today's Deliveries</Button>
        </div>
      ) : (
        <>
          <CountersBar total={totalEligible} assigned={assigned} left={left} current={current} />
          <RouteTabsList tempRoutes={tempRoutes} activeTempId={activeTempId} onSelect={setActiveTempId} onHover={setHighlightedTempId} onNewRoute={() => setNewRouteOpen(true)} onPlanAutomatically={() => setAutoPlanOpen(true)} mode={mode} onAutoAssign={handleAutoAssign} autoAssigning={autoAssigning} onOptimizeAll={handleOptimizeAll} optimizingAll={optimizingAll} />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_380px]">
            <div className="h-[560px] overflow-hidden rounded-md border border-border lg:sticky lg:top-20 lg:self-start" style={{ isolation: "isolate" }} data-testid="builder-map-container">
              {loadingDeliveries ? (
                <div className="flex h-full items-center justify-center text-sm text-text-muted">Loading map...</div>
              ) : (
                <BuilderMap
                  outlet={outlet} deliveries={deliveries} ownerMap={ownerMap} tempRoutes={tempRoutes}
                  activeTempId={activeTempId} drawActive={drawActive} onPolygonComplete={handlePolygonComplete}
                  onMarkerAction={handleMarkerAction} onMarkerDrop={handleMarkerDrop} highlightedTempId={highlightedTempId} previewEndpoints={previewEndpoints}
                  onSelectRoute={setActiveTempId} onSelectSavedRoute={handleSelectSavedRouteFromMap}
                  hoveredDeliveryId={hoveredDeliveryId} routeColorMap={routeColorMap} editingRouteIds={editingRouteIds}
                />
              )}
            </div>

            <div className="space-y-4">
              {autoPlanOpen ? (
                <AutoPlanPanel
                  date={date} onGenerated={handleAutoPlanGenerated} driversFull={drivers} usedDriverIds={tempRoutes.map((r) => r.driverId)}
                  onPreviewChange={setPreviewEndpoints} onClose={() => setAutoPlanOpen(false)}
                  suggestions={suggestions} loading={suggestionsLoading}
                />
              ) : (
                <ActiveRoutePanel
                  route={activeRoute} deliveryIndex={deliveryIndex} drawActive={drawActive}
                  onToggleDraw={() => setDrawActive((v) => !v)}
                  onSetStartTime={(v) => updateActiveRoute((r) => ({ ...r, startTime: v }))}
                  onSetObjective={(v) => updateActiveRoute((r) => ({ ...r, objective: v, optimized: null }))}
                  onSetUseRealDistance={(v) => updateActiveRoute((r) => ({ ...r, useRealDistance: v, optimized: null }))}
                  onSetServiceTimeMinutes={(v) => updateActiveRoute((r) => ({ ...r, serviceTimeMinutes: v, optimized: null }))}
                  onSetMaxDistanceKm={(v) => updateActiveRoute((r) => ({ ...r, maxDistanceKm: v, optimized: null }))}
                  onSetMaxDurationHours={(v) => updateActiveRoute((r) => ({ ...r, maxDurationHours: v, optimized: null }))}
                  onRemoveDelivery={(id) => handleMarkerAction(id, "remove")}
                  onOptimize={() => handleOptimize(1)} onReoptimize={handleReoptimize} optimizing={optimizing} onDeleteRoute={handleDeleteRoute}
                  outletReady={Boolean(outlet?.latitude && outlet?.longitude)}
                  onSaveChanges={handleSaveExistingRouteChanges} onCancelEditing={handleCancelEditing} savingChanges={savingChanges}
                />
              )}
              <UnassignedList deliveries={unassignedDeliveries} tempRoutes={tempRoutes} onHover={setHoveredDeliveryId} onAssign={handleAssignToRoute} />
              <DailySummaryPanel tempRoutes={tempRoutes.filter((r) => !r.existingRouteId)} unassignedCount={left} onFinalize={() => setFinalizeOpen(true)} finalizing={false} />
              <SavedRoutesPanel
                routes={savedRoutes.items} loading={savedRoutes.loading} onEdit={handleEditExistingRoute}
                onDelete={handleDeleteSavedRoute} editingRouteIds={editingRouteIds} routeColorMap={routeColorMap}
              />
            </div>
          </div>
        </>
      )}

      <NewRouteDialog open={newRouteOpen} onOpenChange={setNewRouteOpen} drivers={drivers} usedDriverIds={usedDriverIds} onCreate={handleCreateRoute} />

      <FinalizeRoutesDialog
        open={finalizeOpen} onOpenChange={setFinalizeOpen} tempRoutes={tempRoutes.filter((r) => !r.existingRouteId)} date={date} outlet={outlet}
        onSuccess={(routeIds) => {
          setFinalizeOpen(false);
          setTempRoutes([]);
          setActiveTempId(null);
          toast.success(`Finalized ${routeIds.length} route(s) for ${date}`);
          loadForDate(date);
        }}
      />

      <ConfirmDialog
        open={!!pendingDate} onOpenChange={(v) => !v && setPendingDate(null)}
        title="Discard unsaved route plan?"
        description="Changing the date will clear all temporary routes you've drawn on the map that haven't been finalized yet."
        confirmLabel="Discard & Continue" variant="danger" onConfirm={confirmDateChange}
        testId="discard-plan-confirm-dialog"
      />

      <ConfirmDialog
        open={!!routeDeleteTarget} onOpenChange={(v) => !v && setRouteDeleteTarget(null)}
        title={routeDeleteTarget?.routeId ? `Delete Route #${routeDeleteTarget.routeId}?` : "Delete this draft route?"}
        description={routeDeleteTarget?.routeId
          ? "All of this route's stops will return to the unassigned pool for this date and can be re-optimized/reassigned. This cannot be undone."
          : "This unsaved route and its current stop arrangement will be discarded. The stops stay in the unassigned pool."}
        confirmLabel="Delete Route" variant="danger" loading={deletingRoute} onConfirm={confirmDeleteRoute}
        testId="builder-delete-route-confirm-dialog"
      />
    </div>
  );
}
